import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
    Bell, ChevronLeft, ChevronRight, Clock, Eraser, GripVertical, Info, MousePointerClick,
    QrCode, Sun, Moon, Volume2, VolumeX, X,
} from "lucide-react";
import { DemoCard, useGuide } from "./Guide";
import { COMICS, MERCH, NYC, STREET } from "./assets";

/* ---- tiny WebAudio blip, no assets needed ---- */
let audioCtx = null;
function blip(on, freq = 660) {
    if (!on) return;
    try {
        audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
        const o = audioCtx.createOscillator();
        const g = audioCtx.createGain();
        o.frequency.value = freq;
        o.type = "triangle";
        g.gain.value = 0.05;
        o.connect(g); g.connect(audioCtx.destination);
        o.start();
        g.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + 0.16);
        o.stop(audioCtx.currentTime + 0.17);
    } catch { /* no audio in this browser */ }
}

export function SoundToggleDemo() {
    const { say } = useGuide();
    const [on, setOn] = useState(false);
    return (
        <DemoCard title="Sound on tap" hint="turn it on, then tap" testid="demo-sound">
            <div className="flex flex-wrap items-center gap-3">
                <button onClick={() => { setOn(!on); blip(!on, 520); say(!on ? "Sound is on — now tap the pads. Subtle audio on key actions makes a site feel like an app." : "Muted. Always give people the switch — never autoplay noise at them.", "sound"); }}
                    className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold ${on ? "bg-brand-green text-black" : "border border-white/15 text-zinc-300"}`} data-testid="demo-sound-toggle">
                    {on ? <Volume2 size={15} /> : <VolumeX size={15} />} {on ? "Sound on" : "Sound off"}
                </button>
                {[440, 587, 784].map((f, i) => (
                    <button key={f} onClick={() => { blip(on, f); say(on ? "That's a click people feel. Great for kiosks, games, checkout confirmations." : "Flip the sound on first — top-left button.", "sound-pad"); }}
                        className="h-10 w-10 rounded-xl border border-white/15 font-mono text-[11px] text-zinc-300 hover:border-[color:var(--chapter-accent)]" data-testid={`demo-sound-pad-${i}`}>
                        {i + 1}
                    </button>
                ))}
            </div>
        </DemoCard>
    );
}

export function ScrollStoryDemo() {
    const { say } = useGuide();
    const [step, setStep] = useState(0);
    const beats = [
        ["Day 1", "You text us a mess of ideas.", NYC[0]],
        ["Day 3", "You approve a design you didn't think you could afford.", STREET[1]],
        ["Day 7", "You're live, taking orders, looking like a national brand.", MERCH[0]],
    ];
    return (
        <DemoCard title="Scroll-driven story" hint="scroll inside" span="lg:col-span-2" testid="demo-scroll-story">
            <div onScroll={(e) => {
                const i = Math.min(beats.length - 1, Math.floor(e.currentTarget.scrollTop / 120));
                if (i !== step) { setStep(i); say("Scroll-driven storytelling — the page reveals your pitch one beat at a time instead of dumping a wall of text.", "scrollstory"); }
            }}
                className="h-44 overflow-y-auto rounded-2xl border border-white/10 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <div className="relative h-[520px]">
                    <div className="sticky top-0 h-44">
                        <img src={beats[step][2]} alt="" className="h-full w-full rounded-2xl object-cover opacity-45" />
                        <div className="absolute inset-0 flex flex-col justify-center p-5">
                            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[color:var(--chapter-accent)]">{beats[step][0]}</p>
                            <p className="mt-2 max-w-sm font-display text-xl font-black leading-snug">{beats[step][1]}</p>
                            <p className="mt-3 font-mono text-[11px] uppercase tracking-widest text-zinc-500">keep scrolling ↓</p>
                        </div>
                    </div>
                </div>
            </div>
        </DemoCard>
    );
}

export function ComicReaderDemo() {
    const { say } = useGuide();
    const [i, setI] = useState(0);
    const script = [
        "Our hero's website is from 2011. It has a hit counter.",
        "A rival shop starts ranking above him. He panics.",
        "He calls 13th Ave. We say: seven days.",
        "Day 7 — new site, online orders, rival forgotten. THE END.",
    ];
    const go = (d) => { setI((x) => (x + d + script.length) % script.length); say("A real comic reader — panels, captions, arrows. Museums, artists and merch brands eat this up.", "comicreader"); };
    return (
        <DemoCard title="Comic reader" hint="flip pages" span="lg:col-span-2" testid="demo-comic-reader">
            <div className="relative overflow-hidden rounded-2xl border-2 border-white/20">
                <img src={COMICS[i]} alt="" className="h-52 w-full object-cover" />
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-3 bg-black/85 px-3 py-2">
                    <button onClick={() => go(-1)} className="rounded-full bg-white/10 p-1.5 hover:bg-[color:var(--chapter-accent)] hover:text-black" data-testid="demo-comic-prev"><ChevronLeft size={14} /></button>
                    <p className="flex-1 text-center font-mono text-[11px] uppercase tracking-widest text-zinc-200">{script[i]}</p>
                    <button onClick={() => go(1)} className="rounded-full bg-white/10 p-1.5 hover:bg-[color:var(--chapter-accent)] hover:text-black" data-testid="demo-comic-next"><ChevronRight size={14} /></button>
                </div>
            </div>
        </DemoCard>
    );
}

export function LightModeDemo() {
    const { say } = useGuide();
    const [light, setLight] = useState(true);
    return (
        <DemoCard title="Light mode, done right" hint="flip it" testid="demo-lightmode">
            <div className={`rounded-2xl border p-5 transition-colors duration-500 ${light ? "border-black/10 bg-zinc-100" : "border-white/10 bg-black"}`}>
                <p className={`font-display text-lg font-black ${light ? "text-zinc-900" : "text-white"}`}>Brooklyn Print Co.</p>
                <p className={`mt-1 text-xs ${light ? "text-zinc-600" : "text-zinc-400"}`}>Most shops only get one mood. You get both, tuned properly.</p>
                <button onClick={() => { setLight(!light); say("Light and dark, both designed — not one theme with the colors flipped and everything unreadable.", "lightmode"); }}
                    className={`mt-3 inline-flex items-center gap-2 rounded-full px-4 py-2 text-[11px] font-black uppercase tracking-widest ${light ? "bg-zinc-800 text-zinc-50" : "bg-white text-black"}`} data-testid="demo-lightmode-btn">
                    {light ? <Moon size={13} /> : <Sun size={13} />} {light ? "Go dark" : "Go light"}
                </button>
            </div>
        </DemoCard>
    );
}

export function ScratchRevealDemo() {
    const { say } = useGuide();
    const [pct, setPct] = useState(0);
    return (
        <DemoCard title="Scratch to reveal" hint="drag across" testid="demo-scratch">
            <div className="relative h-28 overflow-hidden rounded-2xl border border-white/10"
                onPointerMove={(e) => { if (e.buttons || e.pointerType === "touch") { const r = e.currentTarget.getBoundingClientRect(); setPct(Math.max(pct, ((e.clientX - r.left) / r.width) * 100)); if (pct > 40) say("Scratch cards, spin wheels, mystery coupons — promo mechanics that get people to come back.", "scratch"); } }}
                data-testid="demo-scratch-area">
                <div className="absolute inset-0 flex items-center justify-center bg-[color:var(--chapter-tint)]">
                    <p className="font-display text-xl font-black text-[color:var(--chapter-accent)]">15% OFF FIRST ORDER</p>
                </div>
                <div className="absolute inset-y-0 right-0 flex items-center justify-center bg-zinc-700 transition-[width] duration-100" style={{ width: `${100 - pct}%` }}>
                    <span className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-[#e4e4e7]"><Eraser size={12} /> scratch</span>
                </div>
            </div>
        </DemoCard>
    );
}

export function DragDropDemo() {
    const { say } = useGuide();
    const [items, setItems] = useState(["Front print", "Back print", "Left sleeve", "Neck label"]);
    const drag = useRef(null);
    return (
        <DemoCard title="Drag to reorder" hint="drag the rows" testid="demo-dragdrop">
            <div className="space-y-2">
                {items.map((it, i) => (
                    <div key={it} draggable onDragStart={() => (drag.current = i)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => {
                            const next = [...items];
                            next.splice(i, 0, next.splice(drag.current, 1)[0]);
                            setItems(next);
                            say("Drag-and-drop ordering — print placements, menu sections, photo galleries. Your staff can rearrange the site without calling us.", "dragdrop");
                        }}
                        className="flex cursor-grab items-center gap-2 rounded-xl border border-white/12 bg-black/40 px-3 py-2.5 text-sm active:cursor-grabbing"
                        data-testid={`demo-drag-${i}`}>
                        <GripVertical size={14} className="text-zinc-500" /> {it}
                    </div>
                ))}
            </div>
        </DemoCard>
    );
}

export function StickyCardsDemo() {
    const { say } = useGuide();
    const cards = [["Faster", "Live in 7 days"], ["Cheaper", "A few thousand, not fifty"], ["Yours", "You own the code"]];
    return (
        <DemoCard title="Sticky stacking cards" hint="scroll inside" testid="demo-sticky">
            <div onScroll={() => say("Cards that stack as you scroll — the trick every premium site uses to make three points land.", "sticky")}
                className="h-40 overflow-y-auto rounded-2xl border border-white/10 p-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {cards.map(([t, s], i) => (
                    <div key={t} className="sticky mb-3 rounded-2xl border border-white/12 bg-[color:var(--chapter-tint)] p-4 backdrop-blur" style={{ top: i * 10 }}>
                        <p className="font-display text-lg font-black">{t}</p>
                        <p className="text-xs text-zinc-400">{s}</p>
                    </div>
                ))}
                <div className="h-24" />
            </div>
        </DemoCard>
    );
}

export function CountdownDemo() {
    const { say } = useGuide();
    const [s, setS] = useState(3 * 3600 + 42 * 60 + 7);
    useEffect(() => { const t = setInterval(() => setS((x) => (x > 0 ? x - 1 : 0)), 1000); return () => clearInterval(t); }, []);
    const fmt = (n) => String(Math.floor(n)).padStart(2, "0");
    return (
        <DemoCard title="Deadline countdown" hint="it's really ticking" testid="demo-countdown">
            <div onMouseEnter={() => say("Cut-off timers. 'Order in 3h 42m for same-day' — this one line has sold more shirts than any ad.", "countdown")}>
                <p className="font-mono text-3xl font-black tabular-nums text-[color:var(--chapter-accent)]">
                    {fmt(s / 3600)}:{fmt((s % 3600) / 60)}:{fmt(s % 60)}
                </p>
                <p className="mt-1 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-zinc-500"><Clock size={11} /> left for same-day pickup</p>
            </div>
        </DemoCard>
    );
}

export function TooltipDemo() {
    const { say } = useGuide();
    const [show, setShow] = useState(false);
    return (
        <DemoCard title="Helpful tooltips" hint="hover the ?" testid="demo-tooltip">
            <div className="relative inline-flex items-center gap-2 text-sm text-zinc-300">
                DTF gang sheet
                <button onMouseEnter={() => { setShow(true); say("Tooltips explain your jargon at the exact moment people get confused. Fewer 'what does that mean' texts.", "tooltip"); }}
                    onMouseLeave={() => setShow(false)} className="rounded-full border border-white/20 p-1" data-testid="demo-tooltip-btn"><Info size={12} /></button>
                <AnimatePresence>
                    {show && (
                        <motion.span initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                            className="absolute left-0 top-8 z-10 w-56 rounded-xl border border-white/12 bg-black/95 p-3 text-xs text-zinc-300">
                            One big sheet of transfers, ganged together so you pay by the foot instead of per design.
                        </motion.span>
                    )}
                </AnimatePresence>
            </div>
        </DemoCard>
    );
}

export function ModalDemo() {
    const { say } = useGuide();
    const [open, setOpen] = useState(false);
    return (
        <DemoCard title="Modal that converts" hint="open it" testid="demo-modal">
            <button onClick={() => { setOpen(true); say("Exit-intent and offer modals. Used once, in the right place, they lift orders — used badly they're spam. We know the difference.", "modal"); }}
                className="rounded-xl bg-[color:var(--chapter-accent)] px-5 py-2.5 text-sm font-black uppercase tracking-wide text-black" data-testid="demo-modal-btn">
                Get the offer
            </button>
            <AnimatePresence>
                {open && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 p-6" onClick={() => setOpen(false)} data-testid="demo-modal-overlay">
                        <motion.div initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} className="relative w-full max-w-sm rounded-3xl border border-white/12 bg-brand-ink p-7 text-center">
                            <button className="absolute right-4 top-4 text-zinc-500"><X size={16} /></button>
                            <p className="font-display text-2xl font-black">Wait — 15% off?</p>
                            <p className="mt-2 text-sm text-zinc-400">Drop your number and we'll text the code. That's the whole popup.</p>
                            <p className="mt-4 font-mono text-[11px] uppercase tracking-widest text-zinc-500">tap anywhere to close</p>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </DemoCard>
    );
}

export function ToastStackDemo() {
    const { say } = useGuide();
    const [rows, setRows] = useState([]);
    return (
        <DemoCard title="Stacked toasts" hint="fire a few" testid="demo-toasts">
            <button onClick={() => { const t = { id: Date.now(), text: ["Saved", "Proof sent", "Payment received", "Design uploaded"][rows.length % 4] }; setRows((r) => [...r, t]); say("Feedback for every action, stacked and self-dismissing. Nothing on your site should ever feel silent.", "toasts"); setTimeout(() => setRows((r) => r.filter((x) => x.id !== t.id)), 2600); }}
                className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-4 py-2.5 text-sm font-bold" data-testid="demo-toast-btn">
                <Bell size={14} /> Do something
            </button>
            <div className="mt-3 space-y-1.5">
                <AnimatePresence>
                    {rows.map((r) => (
                        <motion.p key={r.id} initial={{ opacity: 0, x: 18 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, scale: 0.95 }}
                            className="rounded-xl border border-white/12 bg-black/60 px-3 py-2 text-xs">{r.text} ✓</motion.p>
                    ))}
                </AnimatePresence>
            </div>
        </DemoCard>
    );
}

export function FlipCardDemo() {
    const { say } = useGuide();
    const [flip, setFlip] = useState(false);
    return (
        <DemoCard title="Flip card" hint="click to flip" testid="demo-flip">
            <div className="h-28 [perspective:900px]" onClick={() => { setFlip(!flip); say("Flip cards hide the details until they're wanted. Menus, specs, team bios, price breakdowns.", "flip"); }} data-testid="demo-flip-btn">
                <motion.div animate={{ rotateY: flip ? 180 : 0 }} transition={{ duration: 0.6 }} className="relative h-full w-full [transform-style:preserve-3d]">
                    <div className="absolute inset-0 flex items-center justify-center rounded-2xl border border-white/12 bg-[color:var(--chapter-tint)] font-display text-lg font-black [backface-visibility:hidden]">Premium Tee</div>
                    <div className="absolute inset-0 flex flex-col items-center justify-center rounded-2xl border border-white/12 bg-black/70 [backface-visibility:hidden] [transform:rotateY(180deg)]">
                        <p className="font-mono text-sm text-[color:var(--chapter-accent)]">$18 · 6oz cotton</p>
                        <p className="mt-1 text-xs text-zinc-400">Same-day print, S–3XL</p>
                    </div>
                </motion.div>
            </div>
        </DemoCard>
    );
}

export function SplitHoverDemo() {
    const { say } = useGuide();
    const [side, setSide] = useState(null);
    return (
        <DemoCard title="Split hover panels" hint="hover each half" span="lg:col-span-2" testid="demo-split">
            <div className="flex h-28 gap-2">
                {[["Shirts", MERCH[1]], ["Signs", STREET[2]]].map(([label, img], i) => (
                    <div key={label} onMouseEnter={() => { setSide(i); say("Two doors, one screen. Send shirt customers one way and sign customers the other, instantly.", "split"); }}
                        onMouseLeave={() => setSide(null)}
                        className="relative flex-1 cursor-pointer overflow-hidden rounded-2xl transition-all duration-500"
                        style={{ flexGrow: side === i ? 2 : 1 }} data-testid={`demo-split-${i}`}>
                        <img src={img} alt="" className="h-full w-full object-cover" />
                        <span className="absolute inset-0 flex items-center justify-center bg-black/50 font-display text-lg font-black text-[#ffffff]">{label}</span>
                    </div>
                ))}
            </div>
        </DemoCard>
    );
}

export function FilterChipsDemo() {
    const { say } = useGuide();
    const all = [["Tees", "shirt"], ["Hoodies", "shirt"], ["Banners", "sign"], ["Decals", "sign"], ["Gang sheets", "dtf"], ["Tote bags", "shirt"]];
    const [f, setF] = useState("all");
    const shown = f === "all" ? all : all.filter(([, k]) => k === f);
    return (
        <DemoCard title="Filter chips" hint="filter it" testid="demo-filters">
            <div className="flex flex-wrap gap-2">
                {["all", "shirt", "sign", "dtf"].map((k) => (
                    <button key={k} onClick={() => { setF(k); say("Instant filtering, no page reloads. Big catalogs stay browsable on a phone.", "filters"); }}
                        className={`rounded-full px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest ${f === k ? "bg-[color:var(--chapter-accent)] text-black" : "border border-white/15 text-zinc-400"}`} data-testid={`demo-filter-${k}`}>{k}</button>
                ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
                {shown.map(([label]) => <span key={label} className="rounded-xl border border-white/12 bg-black/40 px-3 py-1.5 text-xs">{label}</span>)}
            </div>
        </DemoCard>
    );
}

export function LoyaltyCardDemo() {
    const { say } = useGuide();
    const [n, setN] = useState(3);
    return (
        <DemoCard title="Loyalty punch card" hint="punch it" testid="demo-loyalty">
            <div className="flex items-center gap-3">
                <span className="rounded-xl border border-white/12 bg-black/50 p-2.5 text-[color:var(--chapter-accent)]"><QrCode size={20} /></span>
                <div className="flex gap-1.5">
                    {[...Array(6)].map((_, i) => (
                        <span key={i} className={`h-5 w-5 rounded-full border ${i < n ? "border-transparent bg-[color:var(--chapter-accent)]" : "border-white/20"}`} />
                    ))}
                </div>
                <button onClick={() => { setN((x) => (x >= 6 ? 0 : x + 1)); say(n >= 5 ? "Six punches, free shirt — and you own the customer list, not a third-party app." : "Digital punch cards and QR check-ins. Repeat customers, tracked automatically.", "loyalty"); }}
                    className="ml-auto rounded-xl border border-white/15 px-3 py-2 text-xs font-bold" data-testid="demo-loyalty-btn">Punch</button>
            </div>
            <p className="mt-2 text-xs text-zinc-500">{n >= 6 ? "Free shirt unlocked." : `${6 - n} to go`}</p>
        </DemoCard>
    );
}

export function TabsDemo() {
    const { say } = useGuide();
    const tabs = { Pricing: "$15 white tees, $5/ft DTF, signs from $95.", Turnaround: "Same-day on white tees. 3–5 days on signs.", Pickup: "1309 78th St, Brooklyn — 24hr locker available." };
    const keys = Object.keys(tabs);
    const [t, setT] = useState(keys[0]);
    return (
        <DemoCard title="Tabbed info" hint="switch tabs" testid="demo-tabs">
            <div className="flex gap-1.5">
                {keys.map((k) => (
                    <button key={k} onClick={() => { setT(k); say("Tabs keep a page short. Everything's there — nothing's shouting at once.", "tabs"); }}
                        className={`rounded-lg px-3 py-1.5 text-xs font-bold ${t === k ? "bg-white/10 text-white" : "text-zinc-500"}`} data-testid={`demo-tab-${k.toLowerCase()}`}>{k}</button>
                ))}
            </div>
            <p className="mt-3 text-sm text-zinc-300">{tabs[t]}</p>
        </DemoCard>
    );
}

export function HotspotDemo() {
    const { say } = useGuide();
    const [pin, setPin] = useState(null);
    const pins = [["Front print", 35, 40], ["Neck label", 50, 18], ["Sleeve hit", 74, 52]];
    return (
        <DemoCard title="Image hotspots" hint="tap the dots" testid="demo-hotspots">
            <div className="relative h-32 overflow-hidden rounded-2xl border border-white/10">
                <img src={MERCH[2]} alt="" className="h-full w-full object-cover" />
                {pins.map(([label, x, y], i) => (
                    <button key={label} onClick={() => { setPin(i); say("Clickable hotspots on a photo — show what's what without a paragraph of text.", "hotspots"); }}
                        className={`absolute h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 ${pin === i ? "border-black bg-[color:var(--chapter-accent)]" : "border-white bg-black/70"}`}
                        style={{ left: `${x}%`, top: `${y}%` }} data-testid={`demo-hotspot-${i}`} />
                ))}
                {pin !== null && <p className="absolute inset-x-0 bottom-0 bg-black/85 px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-[color:var(--chapter-accent)]">{pins[pin][0]}</p>}
            </div>
        </DemoCard>
    );
}

export function StatBarsDemo() {
    const { say } = useGuide();
    const rows = [["Same-day jobs", 92], ["Repeat customers", 74], ["Rush jobs on time", 98]];
    return (
        <DemoCard title="Animated stat bars" hint="hover" testid="demo-statbars">
            <div className="space-y-3" onMouseEnter={() => say("Numbers that fill in as they appear. Show off your on-time rate, your reviews, your volume.", "statbars")}>
                {rows.map(([label, v]) => (
                    <div key={label}>
                        <p className="flex justify-between font-mono text-[11px] uppercase tracking-widest text-zinc-400"><span>{label}</span><span>{v}%</span></p>
                        <div className="mt-1 h-2 overflow-hidden rounded-full bg-white/10">
                            <motion.div initial={{ width: 0 }} whileInView={{ width: `${v}%` }} viewport={{ once: true }} transition={{ duration: 1 }} className="h-full rounded-full bg-[color:var(--chapter-accent)]" />
                        </div>
                    </div>
                ))}
            </div>
        </DemoCard>
    );
}

export function GlowCursorButtonDemo() {
    const { say } = useGuide();
    const [p, setP] = useState({ x: 50, y: 50 });
    return (
        <DemoCard title="Cursor-aware button" hint="hover slowly" testid="demo-glowcursor">
            <button onMouseMove={(e) => { const r = e.currentTarget.getBoundingClientRect(); setP({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 }); }}
                onClick={() => say("The light follows the cursor inside the button. This is the kind of detail people screenshot.", "glowcursor")}
                className="relative w-full overflow-hidden rounded-2xl border border-white/12 py-5 font-display text-lg font-black" data-testid="demo-glowcursor-btn">
                <span className="absolute inset-0" style={{ background: `radial-gradient(140px circle at ${p.x}% ${p.y}%, var(--chapter-accent), transparent 70%)`, opacity: 0.35 }} />
                <span className="relative">Start my project</span>
            </button>
        </DemoCard>
    );
}

export function ClickHeatDemo() {
    const { say } = useGuide();
    const [taps, setTaps] = useState([]);
    return (
        <DemoCard title="We can see what people tap" hint="tap around" testid="demo-heat">
            <div onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setTaps((t) => [...t.slice(-24), { id: Date.now(), x: e.clientX - r.left, y: e.clientY - r.top }]); say("Heatmaps and analytics baked in — you'll know exactly what customers click and what they ignore.", "heat"); }}
                className="relative h-28 cursor-crosshair rounded-2xl border border-dashed border-white/15" data-testid="demo-heat-area">
                {taps.map((t) => <span key={t.id} className="absolute h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[color:var(--chapter-accent)] opacity-25" style={{ left: t.x, top: t.y }} />)}
                <span className="absolute inset-0 flex items-center justify-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-zinc-500"><MousePointerClick size={12} /> tap anywhere</span>
            </div>
        </DemoCard>
    );
}

export function TickerNewsDemo() {
    const { say } = useGuide();
    const [i, setI] = useState(0);
    const items = ["New: UV DTF stickers in stock", "Class open Saturday — 2 seats", "Free local delivery over $250", "Gang sheets now $4/ft for members"];
    useEffect(() => { const t = setInterval(() => setI((x) => (x + 1) % items.length), 2600); return () => clearInterval(t); }, []);
    return (
        <DemoCard title="Announcement rotator" hint="watch it" testid="demo-announce">
            <div onMouseEnter={() => say("One bar you update from your phone, seen on every page. Perfect for hours, deals and holiday notices.", "announce")}
                className="h-12 overflow-hidden rounded-xl border border-white/10 bg-black/40 px-3">
                <AnimatePresence mode="wait">
                    <motion.p key={i} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -20, opacity: 0 }}
                        className="flex h-12 items-center font-mono text-[11px] uppercase tracking-[0.15em] text-[color:var(--chapter-accent)]">{items[i]}</motion.p>
                </AnimatePresence>
            </div>
        </DemoCard>
    );
}
